import * as React from "react";
import PropTypes from "prop-types";
import {
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Button,
  makeStyles,
  OverlayDrawer,
  TabList,
  Tab
} from "@fluentui/react-components";
import { Dismiss24Regular } from "@fluentui/react-icons";
import { FormattedMessage, useIntl } from "react-intl";
import { useSettingsContext } from "../room/SettingsContext";

const useStyles = makeStyles({
  roomSettingsSidebar: {
    width: "100%",
    "@media (min-width: 1200px)": {
      width: "500px"
    }
  },
  header: {
    backgroundColor: "var(--primaryColorBackground)"
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    paddingBottom: "16px"
  },
  tabList: {
    // Make the tab list scrollable, but hide the scrollbar
    overflow: "auto",
    scrollbarWidth: "none",
    scrollbarColor: "transparent",
    "&::-webkit-scrollbar": {
      display: "none"
    }
  }
});

export function RoomSettingsSidebar({ open, onOpenChange, children, onSave, settingsHasChanges }) {
  const styles = useStyles();
  const intl = useIntl();

  const { tabs, selectedTab, onTabChange } = useSettingsContext();

  return (
    <OverlayDrawer
      className={styles.roomSettingsSidebar}
      position="end"
      modalType="non-modal"
      open={open}
      onOpenChange={(_, { open }) => onOpenChange(open)}
    >
      <DrawerHeader className={styles.header}>
        <DrawerHeaderTitle
          action={
            <Button
              appearance="subtle"
              aria-label={intl.formatMessage({ id: "room-settings-sidebar.close-button", defaultMessage: "Close" })}
              icon={<Dismiss24Regular />}
              onClick={() => onOpenChange(false)}
            />
          }
        >
          <FormattedMessage id="room-settings-sidebar.title" defaultMessage="Settings" />
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody>
        <TabList
          selectedValue={selectedTab}
          onTabSelect={(e, data) => onTabChange(data.value)}
          className={styles.tabList}
        >
          {tabs.map(tab => (
            <Tab key={tab.value} value={tab.value}>
              {tab.content}
            </Tab>
          ))}
        </TabList>
        <div className={styles.panels}>
          {children} {/* Render the child component based on the selected tab */}
        </div>
      </DrawerBody>
      {selectedTab === "verse" && (
        <DrawerHeader className={styles.header}>
          <div className={styles.footer}>
            <Button size="medium" appearance="primary" onClick={onSave} disabled={!settingsHasChanges}>
              <FormattedMessage id="room-settings-sidebar.save-button" defaultMessage="Save" />
            </Button>
          </div>
        </DrawerHeader>
      )}
    </OverlayDrawer>
  );
}

RoomSettingsSidebar.propTypes = {
  selectedTab: PropTypes.string.isRequired,
  onTabSelect: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  children: PropTypes.node,
  showVerseSettings: PropTypes.bool,
  onSave: PropTypes.func,
  settingsHasChanges: PropTypes.bool
};
